"use client";

import { motion } from "framer-motion";
import { useMemo } from "react";
import { ProductCard } from "@/components/products/product-card";
import type { ShopifyProduct } from "@/lib/shopify/types";

interface ProductsGridProps {
	products: ShopifyProduct[];
}

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const item = {
	hidden: { opacity: 0, y: 20 },
	show: { opacity: 1, y: 0 },
};

export function ProductsGrid({ products }: ProductsGridProps) {
	// Generate a stable key from product IDs so the grid fully re-mounts
	// and re-animates whenever the filtered product set changes.
	const gridKey = useMemo(
		() => products.map((p) => p.id).join(","),
		[products],
	);

	return (
		<motion.div
			key={gridKey}
			variants={container}
			initial="hidden"
			animate="show"
			className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
		>
			{products.map((product, index) => (
				<motion.div key={product.id} variants={item}>
					<ProductCard
						product={product}
						priority={index < 4}
						className="h-full border border-border/50 hover:border-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
					/>
				</motion.div>
			))}
		</motion.div>
	);
}
